import React, { useEffect, useState } from "react";
import { debounce } from "lodash";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import SiteManager from "../../../types/siteManager";
import { useSelector } from "../../../store";
import SettingsModal from "../../../types/settingsModal";
import { Base } from "../../base";
import AddEditSiteManagerForm from "../../forms/AddEditSiteManagerForm";
import ColumnDef from "../../../types/columnDef";
import SearchContainer from "./SearchContainer";

interface Props {
  apiSiteManagerSearch: (search: SearchParams) => Promise<any>;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  apiGetSiteManager: (id: number) => void;
  resetSiteManager: () => void;
  setSiteManagerFilter: (params: SearchParams) => void;
}

const SiteManagers: React.FC<Props> = (props) => {
  const {
    apiSiteManagerSearch,
    phone,
    setDeleteModal,
    apiGetSiteManager,
    resetSiteManager,
    setSiteManagerFilter,
  } = props;
  const { siteManagerSearchResults, siteManagerFilter, status } = useSelector(
    (store) => store.siteManager
  );
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [addEditModal, setAddEditModal] = useState(false);
  const [highlightId, setHighlightId] = useState(0);

  useEffect(() => {
    if (phone) {
      next();
    }
    return () => {
      setSiteManagerFilter(defaultParams);
    };
  }, [phone]);

  const updateSearch = debounce((query: string) => {
    setSiteManagerFilter({
      ...siteManagerFilter,
      query: query,
      pageNumber: phone ? 2 : 1,
      pageSize: 10,
    });
    apiSiteManagerSearch({
      ...siteManagerFilter,
      query: query,
      pageNumber: 1,
      pageSize: 10,
    }).then((result: any) => {
      setMobileRows(result);
    });
  }, 500);

  const columns: ColumnDef[] = [
    {
      field: "firstName",
      headerName: "First Name",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((siteManager: SiteManager) => {
      let rowObject = {
        id: siteManager.id,
        mobileTitle: `${siteManager.firstName} ${siteManager.lastName}`,
        firstName: siteManager.firstName,
        lastName: siteManager.lastName,
        actions: (
          <Base.TableActions
            id={siteManager.id}
            // TODO: Lock behind when we add roles
            handleDelete={() => {
              apiGetSiteManager(siteManager.id);
              setDeleteModal({ visible: true, modal: "siteManagers" });
              setHighlightId(siteManager.id);
            }}
            handleEdit={() => {
              apiGetSiteManager(siteManager.id);
              setAddEditModal(true);
              setHighlightId(siteManager.id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiSiteManagerSearch({
      ...siteManagerFilter,
      pageNumber,
      pageSize,
    }).then(() => {
      setSiteManagerFilter({
        ...siteManagerFilter,
        pageNumber,
        pageSize,
      });
    });
  };

  const next = () => {
    apiSiteManagerSearch(siteManagerFilter).then((result: any) => {
      setSiteManagerFilter({
        ...siteManagerFilter,
        pageNumber: siteManagerFilter?.pageNumber! + 1,
      });
      setMobileRows([...mobileRows, ...result]);
    });
  };

  const close = () => {
    apiSiteManagerSearch(defaultParams);
    setSiteManagerFilter(defaultParams);
    setAddEditModal(false);
    resetSiteManager();
    setHighlightId(0);
  };

  const handleSaveUpdate = () => {
    apiSiteManagerSearch({ query: "", pageNumber: 1, pageSize: 10 }).then(
      (result: any) => {
        setMobileRows(result);
        setSiteManagerFilter({ ...siteManagerFilter, pageNumber: 2 });
      }
    );
  };

  const getOrderBy = (field: string) => {
    const fieldList = ["firstName", "lastName"];
    return fieldList.find((value) => value === field) ?? undefined;
  };

  return (
    <>
      <SearchContainer
        updateSearch={updateSearch}
        searchValue={siteManagerFilter.query}
        modalOpen={setAddEditModal}
        reset={resetSiteManager}
      >
        <Base.TableContainer
          columns={columns}
          rows={displayRows(siteManagerSearchResults?.results || [])}
          mobileRows={displayRows(mobileRows)}
          page={siteManagerFilter.pageNumber}
          pageSize={siteManagerFilter.pageSize}
          totalRows={siteManagerSearchResults?.totalResults}
          changePage={changePage}
          next={next}
          loading={status === "loading"}
          handleSort={(orderBy, orderDirection) => {
            const orderByField = orderBy ? getOrderBy(orderBy) : undefined;
            apiSiteManagerSearch({
              ...siteManagerFilter,
              orderBy: orderByField,
              orderDirection,
            });
          }}
          highlightId={highlightId}
        />
      </SearchContainer>

      <Base.Modal open={addEditModal} onClose={close}>
        <AddEditSiteManagerForm close={close} onSave={handleSaveUpdate} />
      </Base.Modal>
    </>
  );
};

export default SiteManagers;
